/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */



import { Component, Input, Output, EventEmitter, ElementRef, Renderer, HostBinding, ViewEncapsulation } from '@angular/core';
import { ViewChild, Inject, Optional, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, COMPOSITION_BUFFER_MODE } from '@angular/forms';
import classNames from 'classnames';

import { FormControl } from '../input/form.control';
import { onNextFrame } from '../util/anim.frame';
import KeyCode from '../util/key.code';
/**
 * @param {?} obj
 * @return {?}
 */
function isPresent(obj: any): boolean {
    return obj !== undefined && obj !== null;
}
/**
 * When click and hold on a button - the speed of auto changin the value.
 */
const SPEED = 200;
/**
 * When click and hold on a button - the delay before auto changin the value.
 */
const DELAY = 600;

const /** @type {?} */ INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => InputNumber),
    multi: true
};
export class InputNumber extends FormControl {
/**
 * 样式前缀
 */
prefixCls = 'ant-input-number';
/**
 * 用户CSS样式
 */
class: string;
/**
 * 控件大小。注：标准表单内的输入框大小限制为 `large`。可选 `large` `default` `small`。
 */
size: 'large' | 'default' | 'small' = 'default';
/**
 * 选择框默认文字
 */
placeholder: string;
/**
 * 是否只读状态，默认为 false
 */
readOnly = false;
/**
 * max 属性规定输入字段所允许的最大值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 */
max: number;
/**
 * min 属性规定输入字段所允许的最小值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 */
min: number;

    /** 每次改变步数，可以为小数。默认为1。 */
    // tslint:disable-next-line:no-input-rename
     stepValue = 1;
/**
 * 数值精度
 */
precision: number;
/**
 * 指定输入框展示值的格式。入参为(value: number)，返回值为string。
 */
formatter: (value: number) => string;
/**
 * 指定从 formatter 里转换回数字的方式，和 formatter 搭配使用
 */
parser: (formated: string) => number;
/**
 * 自动聚焦
 */
autoFocus = false;
/**
 * 值变更事件
 */
change = new EventEmitter<any>();
/**
 * 实际输入框
 */
input: ElementRef;
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input no longer has focus
 */
blur: EventEmitter<Event> = new EventEmitter<Event>();
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input has focus
 */
focus: EventEmitter<Event> = new EventEmitter<Event>();
/**
 * The internal data model
 * 
 * \@internal
 */
private _value: any;
private actualValue: number;
/**
 * 是否已聚焦
 */
public focused = false;
private autoStepTimer: any;
/**
 * @param {?} renderer
 * @param {?} elementRef
 * @param {?} compositionMode
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef,
private compositionMode: boolean) {

        super(renderer, elementRef, compositionMode);
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this.elementRef.nativeElement as HTMLElement));
    }
/**
 * 样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ classes = classNames(this.prefixCls, {
            [`${this.prefixCls}-lg`]: this.size === 'large',
            [`${this.prefixCls}-sm`]: this.size === 'small',
            [`${this.prefixCls}-disabled`]: this.disabled,
            [`${this.prefixCls}-focused`]: this.focused
        }, this.class);
        return classes;
    }
/**
 * 内部输入框的内容，用于绑定内部的 ngModel 值
 * @param {?} value
 * @return {?}
 */
set innerValue(value: any) {
        if (this._value !== value) {
            this._value = value;
            // processValue()方法用于转换接收内部input控件产生的值
            const /** @type {?} */ actualValue: number = this.processValue(value);
            if (isNaN(actualValue)) {
                return;
            }

            this.writeValue(actualValue);

            if (this.actualValue !== actualValue) {
                this.actualValue = actualValue;

                // view1 -> model -> onChange -> view2 (outside world) (ie. NgModel on this control)
                // view1 is 我们在template中定义的组件生成的view
                // view2 is 别人使用当前组件在应用中生成的view
                this.onChange(actualValue);
                this.change.emit(actualValue);
            }
        }
    }
/**
 * 对用户输入的值进行预处理，如文本转数字等
 * @param {?} value
 * @return {?}
 */
processValue(value: any): number {
        if (this.parser) {
            value = this.parser(value);
        }
        return Number(value);
    }
/**
 * The value of the input ngModel
 * 
 * \@internal (model -> view2)
 * @return {?}
 */
get innerValue() {
        return this._value;
    }
/**
 * Write a new value to the element.
 * 
 * \@Override (From ControlValueAccessor interface)
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
        if (value == null) {
            return;
        }

        if (!isPresent(this.actualValue)) { // 初始化通过ngModel接收的数值
            if (typeof value === 'number') {
                this.actualValue = value;
            }
        }

        // 设置带格式的显示数值
        const /** @type {?} */ formatedValue = this.formatNumber(value);
        if (formatedValue !== this.innerValue) {
            onNextFrame(() => {
                // 注意：直接设置`_value`，而不是设置innerValue
                this._value = formatedValue;
            });
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onBlur(event: UIEvent) {
        this.onTouched(); // set your control to 'touched'
        this.focused = false;
        this.blur.emit(event);
    }
/**
 * @param {?} event
 * @return {?}
 */
onFocus(event: Event): void {
        this.onTouched(); // set your control to 'touched'
        this.focused = true;
        this.focus.emit(event);
    }
/**
 * focus on the input
 * @return {?}
 */
doFocus(): void {
        this.input.nativeElement.focus();
    }
/**
 * @param {?} event
 * @return {?}
 */
onKeyDown(event: KeyboardEvent): void {
        if (event.keyCode === KeyCode.UP) {
            const /** @type {?} */ ratio = this.getRatio(event);
            this.up(event, ratio);
            this.stop();
        } else if (event.keyCode === KeyCode.DOWN) {
            const /** @type {?} */ ratio = this.getRatio(event);
            this.down(event, ratio);
            this.stop();
        }
    }
/**
 * @param {?} event
 * @return {?}
 */
onKeyUp(event: KeyboardEvent): void {
        this.stop();
    }
/**
 * @param {?} event
 * @return {?}
 */
onUpHandlerPress(event: Event): void {
        this.up(event);
    }
/**
 * @param {?} event
 * @return {?}
 */
onDownHandlerPress(event: Event): void {
        this.down(event);
    }
/**
 * 禁止内部input控件的change事件传播
 * @param {?} event
 * @return {?}
 */
onInputChange(event: Event): void {
        event.stopPropagation();
    }
/**
 * 若按下『SHIFT』键则10倍速率，若按下『CTRL』或『META』键，则0.1倍速率，否则速率不变
 * 
 * @param {?} e 键盘事件
 * @return {?}
 */
getRatio(e: KeyboardEvent): number {
        let /** @type {?} */ ratio = 1;
        if (e.metaKey || e.ctrlKey) {
            ratio = 0.1;
        } else if (e.shiftKey) {
            ratio = 10;
        }
        return ratio;
    }
/**
 * @param {?} num
 * @return {?}
 */
formatNumber(num: number): string {
        if (this.formatter) {
            return this.formatter(num);
        }
        return num.toFixed(this.getMaxPrecision());
    }
/**
 * 向上增加的按钮是否不可用
 * @return {?}
 */
isUpHandlerDisabled(): boolean {
        if (isNaN(this.actualValue)) {
            return true;
        }
        return isPresent(this.max) && this.actualValue >= this.max;
    }
/**
 * 向下增加的按钮是否不可用
 * @return {?}
 */
isDownHandlerDisabled(): boolean {
        if (isNaN(this.actualValue)) {
            return true;
        }
        return isPresent(this.min) && this.actualValue <= this.min;
    }
/**
 * @param {?} event
 * @return {?}
 */
preventDefaultEvent(event: Event): void {
        event.preventDefault();
    }
/**
 * @param {?=} event
 * @return {?}
 */
stop(event?: Event): void {
        if (this.autoStepTimer) {
            clearTimeout(this.autoStepTimer);
        }
    }
/**
 * @param {?} e
 * @param {?=} ratio
 * @param {?=} recursive
 * @return {?}
 */
down(e: Event, ratio: number = 1, recursive: boolean = false): void {
        if (this.disabled) {
            return;
        }

        this.stop();
        this.step('down', e, ratio);
        this.autoStepTimer = setTimeout(() => {
            this.down(e, ratio, true);
        }, recursive ? SPEED : DELAY);
    }
/**
 * @param {?} e
 * @param {?=} ratio
 * @param {?=} recursive
 * @return {?}
 */
up(e: Event, ratio: number = 1, recursive: boolean = false): void {
        if (this.disabled) {
            return;
        }

        this.stop();
        this.step('up', e, ratio);
        this.autoStepTimer = setTimeout(() => {
            this.up(e, ratio, true);
        }, recursive ? SPEED : DELAY);
    }
/**
 * @param {?} type
 * @param {?} e
 * @param {?=} ratio
 * @return {?}
 */
step(type: string, e: Event, ratio: number = 1): void {
        if (e) {
            e.preventDefault();
        }

        if (this.disabled) {
            return;
        }
        this.focused = true;

        let /** @type {?} */ value = 0;
        if ('up' === type) {
            value = !isPresent(this.actualValue) ? 0 : this.actualValue + this.stepValue * ratio;
            if (isPresent(this.max) && value >= this.max) {
                value = this.max;
            }
        }
        if ('down' === type) {
            value = !isPresent(this.actualValue) ? 0 : this.actualValue - this.stepValue * ratio;
            if (isPresent(this.min) && value <= this.min) {
                value = this.min;
            }
        }

        if (this.actualValue !== value) {
            this.actualValue = value;
            this.writeValue(value);
            this.change.emit(value);
        }
    }
/**
 * @param {?} value
 * @return {?}
 */
getPrecision(value: any): number {
        if (!isPresent(value)) {
            return 0;
        }

        const /** @type {?} */ str = value.toString();
        if (str.indexOf('e-') >= 0) {
            return parseInt(str.slice(str.indexOf('e-') + 2), 10);
        }

        let /** @type {?} */ precision = 0;
        if (str.indexOf('.') >= 0) {
            precision = str.length - str.indexOf('.') - 1;
        }
        return precision;
    }
/**
 * @return {?}
 */
getMaxPrecision(): number {
        if (isPresent(this.precision)) {
            return this.precision;
        }

        const /** @type {?} */ stepPrecision = this.getPrecision(this.stepValue);
        const /** @type {?} */ valuePrecision = this.getPrecision(this.innerValue);
        return Math.max(valuePrecision, stepPrecision);
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-number',
    templateUrl: './input.number.html',
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [INPUT_CONTROL_VALUE_ACCESSOR],
    exportAs: 'input'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, decorators: [{ type: Inject, args: [Renderer, ] }, ]},
{type: ElementRef, decorators: [{ type: Inject, args: [ElementRef, ] }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [COMPOSITION_BUFFER_MODE, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'class': [{ type: Input },],
'size': [{ type: Input },],
'placeholder': [{ type: Input },],
'readOnly': [{ type: Input },],
'max': [{ type: Input },],
'min': [{ type: Input },],
'stepValue': [{ type: Input, args: ['step', ] },],
'precision': [{ type: Input },],
'formatter': [{ type: Input },],
'parser': [{ type: Input },],
'autoFocus': [{ type: Input },],
'change': [{ type: Output },],
'input': [{ type: ViewChild, args: ['input', ] },],
'blur': [{ type: Output },],
'focus': [{ type: Output },],
'classes': [{ type: HostBinding, args: ['class', ] },],
};
}

function InputNumber_tsickle_Closure_declarations() {
/** @type {?} */
InputNumber.decorators;
/**
 * @nocollapse
 * @type {?}
 */
InputNumber.ctorParameters;
/** @type {?} */
InputNumber.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
InputNumber.prototype.prefixCls;
/**
 * 用户CSS样式
 * @type {?}
 */
InputNumber.prototype.class;
/**
 * 控件大小。注：标准表单内的输入框大小限制为 `large`。可选 `large` `default` `small`。
 * @type {?}
 */
InputNumber.prototype.size;
/**
 * 选择框默认文字
 * @type {?}
 */
InputNumber.prototype.placeholder;
/**
 * 是否只读状态，默认为 false
 * @type {?}
 */
InputNumber.prototype.readOnly;
/**
 * max 属性规定输入字段所允许的最大值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 * @type {?}
 */
InputNumber.prototype.max;
/**
 * min 属性规定输入字段所允许的最小值。<br>
 * 注释：max 和 min 属性适用于以下 <input> 类型：number, range, date, datetime, datetime-local, month, time 以及 week。
 * @type {?}
 */
InputNumber.prototype.min;
/** @type {?} */
InputNumber.prototype.stepValue;
/**
 * 数值精度
 * @type {?}
 */
InputNumber.prototype.precision;
/**
 * 指定输入框展示值的格式。入参为(value: number)，返回值为string。
 * @type {?}
 */
InputNumber.prototype.formatter;
/**
 * 指定从 formatter 里转换回数字的方式，和 formatter 搭配使用
 * @type {?}
 */
InputNumber.prototype.parser;
/**
 * 自动聚焦
 * @type {?}
 */
InputNumber.prototype.autoFocus;
/**
 * 值变更事件
 * @type {?}
 */
InputNumber.prototype.change;
/**
 * 实际输入框
 * @type {?}
 */
InputNumber.prototype.input;
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input no longer has focus
 * @type {?}
 */
InputNumber.prototype.blur;
/**
 * 默认情况下，输入框的focus、blur事件是不传播的，因此当我们控制内部的输入框后，需要发射自己的focus、blur事件
 * 
 * \@output {event} Expression to call when the input has focus
 * @type {?}
 */
InputNumber.prototype.focus;
/**
 * The internal data model
 * 
 * \@internal
 * @type {?}
 */
InputNumber.prototype._value;
/** @type {?} */
InputNumber.prototype.actualValue;
/**
 * 是否已聚焦
 * @type {?}
 */
InputNumber.prototype.focused;
/** @type {?} */
InputNumber.prototype.autoStepTimer;
/** @type {?} */
InputNumber.prototype.renderer;
/** @type {?} */
InputNumber.prototype.elementRef;
/** @type {?} */
InputNumber.prototype.compositionMode;
}



interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
